import { Alert, Stack } from "@mui/material";
import {
  ControlledModificationReasonField,
  useIsModificationReasonSatisfied,
} from "@src/appV2/ExperimentalHomeHealth/components/ModificationReasonField";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { type UseModalState } from "@src/lib/ui-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { getCaseQueryKey } from "../api/useCase";
import { useUpdateVisit } from "../api/useUpdateVisit";
import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { useIsAdmin } from "../../lib/useIsAdmin";
import { type Visit, VISIT_TYPE_LABELS, VisitStatus } from "../types";

interface Props {
  visit: Visit;
  modalState: UseModalState;
}

/**
 * Confirmation for cancelling a visit from the mobile case detail view, matching
 * the desktop CancelVisitDialog: it sets the visit status to CANCELED (with the
 * admin modification reason) and refreshes the case so the list reflects it.
 */
export function MobileCancelVisitDialog({ visit, modalState }: Props) {
  const workplaceId = useCurrentUserId() ?? "";
  const isAdmin = useIsAdmin();
  const queryClient = useQueryClient();
  const { mutateAsync: updateVisit, isLoading } = useUpdateVisit(visit.id);

  const [modificationReason, setModificationReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const isModificationReasonSatisfied = useIsModificationReasonSatisfied(modificationReason);

  // Reset transient state when the dialog closes so reopening starts clean.
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setModificationReason("");
      setError(null);
    }
  }, [modalState.modalIsOpen]);

  const { caseId } = visit.attributes;

  async function handleCancelVisit() {
    setError(null);
    try {
      await updateVisit({
        data: {
          attributes: { status: VisitStatus.CANCELED, modificationReason },
          type: "visit",
        },
      });
      logEvent(APP_V2_USER_EVENTS.HOME_HEALTH_VISIT_CANCELED, {
        workplaceId,
        visitId: visit.id,
        type: visit.attributes.type,
        workerReq: visit.attributes.workerReq,
        bookedWorkerId: visit.attributes.bookedWorkerId,
        invitedWorkersNames: visit.attributes.invitedWorkersNames,
        invitedWorkersIds: visit.attributes.invitedWorkersIds,
        caseId,
        performedByAdmin: isAdmin,
      });
      // The case detail list reads visits from the case query, which the visit
      // mutation doesn't invalidate on its own (mirrors the add/edit visit flows).
      await queryClient.invalidateQueries(getCaseQueryKey({ workplaceId, caseId }));
      modalState.closeModal();
    } catch {
      setError("Something went wrong. Please try again.");
    }
  }

  return (
    <ResponsiveDialog
      modalState={modalState}
      title={`Cancel ${VISIT_TYPE_LABELS[visit.attributes.type]} visit`}
      footerContent={
        <Stack spacing={3} sx={{ paddingX: 5, paddingTop: 3, paddingBottom: 5 }}>
          <Button
            fullWidth
            variant="dangerFilled"
            disabled={isLoading || !isModificationReasonSatisfied}
            onClick={() => {
              void handleCancelVisit();
            }}
          >
            Cancel Visit
          </Button>
          <Button
            fullWidth
            variant="secondary"
            disabled={isLoading}
            onClick={modalState.closeModal}
          >
            Keep Visit
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Text variant="body1">Are you sure you want to cancel this visit?</Text>
        <ControlledModificationReasonField
          value={modificationReason}
          onChange={setModificationReason}
        />
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </ResponsiveDialog>
  );
}
